import { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import '../assets/css/header.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faCalendarAlt, faCaretDown, faSearch, faRss } from '@fortawesome/free-solid-svg-icons';
import { faFacebookF, faTwitter, faLinkedin, faInstagram, faGithub } from '@fortawesome/free-brands-svg-icons';
import { Link } from "react-router-dom";

function Header() {
  const navigate = useNavigate();

  // State for language dropdown
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const languages = ["English", "Spanish", "French", "German", "Hindi"];
  const dropdownRef = useRef(null);

  // State for shrinking navbar
  const [shrink, setShrink] = useState(false);

  // State for location and time
  const [currentCity, setCurrentCity] = useState("Loading...");
  const [currentTime, setCurrentTime] = useState("");
  const [loading, setLoading] = useState(true); // Loading state

  // State for social links and logo
  const [socialLinks, setSocialLinks] = useState({});
  const [websiteLogo, setWebsiteLogo] = useState("");

  // Default logo URL
  const defaultLogo = "path/to/default/logo.png"; // Replace with the path to your default logo image

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Fetch settings from your API
    const fetchSettings = async () => {
      try {
        const response = await fetch('http://localhost:5000/api/settings/'); // Replace with your actual API endpoint
        const data = await response.json();
        setSocialLinks(data.socialLinks); // Extract social links from the response
        setWebsiteLogo(data.websiteLogo); // Extract website logo from the response
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    // Get user location (requires HTTPS)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          // Fetch city name using a geocoding API
          const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
          const data = await response.json();
          setCurrentCity(data.city || "Unknown City");

          // Set current time
          const date = new Date();
          const options = { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: data.timezone };
          setCurrentTime(new Intl.DateTimeFormat('en-US', options).format(date));
          setLoading(false); // Set loading to false after data is fetched
        },
        () => {
          setCurrentCity("Location not available");
          setCurrentTime("");
          setLoading(false); // Set loading to false on error
        }
      );
    } else {
      setCurrentCity("Geolocation not supported");
      setLoading(false); // Set loading to false if geolocation is not supported
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setShrink(true);
      } else {
        setShrink(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLanguageSelect = (lang) => {
    setSelectedLanguage(lang);
    setShowDropdown(false);
  };

  return (
    <div className="header-container">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container">
          <div>
            {loading ? (
              <span>Loading...</span>
            ) : (
              <span>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> {currentCity}
              </span>
            )}
            <span>
              <FontAwesomeIcon icon={faCalendarAlt} /> {currentTime}
            </span>
          </div>
          <div className="right-section">
            <a onClick={() => navigate('/auth')} style={{ cursor: "pointer" }}>
              Login/Register
            </a>

            {/* Language Selector */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown((prev) => !prev)}
                className="language-btn"
                aria-expanded={showDropdown}
              >
                {selectedLanguage} <FontAwesomeIcon icon={faCaretDown} />
              </button>
              {showDropdown && (
                <div className="dropdown-menu">
                  {languages.map((lang, index) => (
                    <div key={index} onClick={() => handleLanguageSelect(lang)} className="dropdown-item">
                      {lang}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="social-icons">
              {socialLinks.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faFacebookF} />
                </a>
              )}
              {socialLinks.twitter && (
                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faTwitter} />
                </a>
              )}
              {socialLinks.linkedin && (
                <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faLinkedin} />
                </a>
              )}
              {socialLinks.instagram && (
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faInstagram} />
                </a>
              )}
              {socialLinks.github && (
                <a href={socialLinks.github} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faGithub} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="header">
        <div className="container">
          <div className="logo">
            <img src={websiteLogo || defaultLogo} alt="Logo" style={{ height: '100px', width: 'auto' }} />
          </div>
          <div className="ad-banner">728x90</div>
        </div>
      </div>

      {/* Navigation Bar */}
      <div className={`navbar ${shrink ? "shrink" : ""}`}>
        <div className="container">
          <div className="menu">
            <Link to="/">HomePage</Link>
            <Link to="/blogs">Blogs</Link>
            <Link to="/national">National</Link>
            <Link to="/AuthorList">Author</Link>
            <Link to="/about">About Us</Link>
            <Link to="/contact">Contact Us</Link>
          </div>
          <div className="search-icon">
            <FontAwesomeIcon icon={faSearch} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Header;